// Browser-only play history and tag-based recommendations (no server, no login).
// History lives in localStorage on the visitor's device; recommendations score
// games from public/reco-index.json (see scripts/build-reco-index.js).
import { normTags } from "./recoTags";

const HISTORY_KEY = "pcg_play_history";
const HISTORY_MAX = 30;
const MOODS_KEY = "pcg_moods";

// Mood tiles shown to first-time visitors; tags are normalised keys from reco-index.json
export const MOODS = [
  { id: "dressup", icon: "/assets/sidenav/v2/dressup.svg", label: "Dress Up", tags: ["dress up", "fashion"] },
  { id: "makeover", icon: "/assets/sidenav/v2/makeup.svg", label: "Makeover & Beauty", tags: ["makeup", "salon"] },
  { id: "aesthetic", icon: "/assets/sidenav/v2/aesthetic.svg", label: "Aesthetic Vibes", tags: ["aesthetic", "goth"] },
  { id: "kpop", icon: "/assets/sidenav/v2/kpop.svg", label: "K-Pop & Celebs", tags: ["k pop", "celebrity"] },
  { id: "princess", icon: "/assets/sidenav/v2/princess.svg", label: "Princess Magic", tags: ["princess", "frozen", "mermaid"] },
  { id: "cozy", icon: "/assets/sidenav/v2/cooking.svg", label: "Cooking & Cozy", tags: ["cooking", "decoration", "cozy", "relaxing", "cleaning"] },
  { id: "puzzle", icon: "/assets/moods/puzzle.svg", label: "Puzzles & Brain", tags: ["puzzle", "brain", "match 3", "merge", "mahjong", "logic"] },
  { id: "action", icon: "/assets/moods/racing.svg", label: "Action & Racing", tags: ["action", "racing", "car", "shooting", ".io", "multiplayer", "adventure"] },
  { id: "memes", icon: "/assets/sidenav/v2/brainrot.svg", label: "Memes & Brainrot", tags: ["brainrot", "roblox", "sprunki"] },
  { id: "pets", icon: "/assets/moods/paw.svg", label: "Pets & Animals", tags: ["animal", "pet", "pony", "unicorn", "dino"] },
];
export const MAX_MOODS = 3;

function readJSON(key, fallback) {
  try {
    const value = JSON.parse(window.localStorage.getItem(key));
    return value ?? fallback;
  } catch (e) {
    return fallback; // private mode / blocked storage
  }
}

// Most recent first: [{ s: slug, n: name, t: thumb, g: tags, v?: video, ts }]
export function getHistory() {
  const history = readJSON(HISTORY_KEY, []);
  return Array.isArray(history) ? history.filter((h) => h && h.s) : [];
}

export function recordPlay(game) {
  const entry = { s: game.name[1], n: game.name[0], t: game.thumb, g: normTags(game.tag), ts: Date.now() };
  if (game.video) entry.v = game.video;
  const history = [entry, ...getHistory().filter((h) => h.s !== entry.s)].slice(0, HISTORY_MAX);
  try {
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
  } catch (e) {
    // storage full or blocked: personalisation just stays off
  }
  return history;
}

export function clearHistory() {
  try {
    window.localStorage.removeItem(HISTORY_KEY);
  } catch (e) {
    // storage blocked: nothing to clear
  }
}

// null = never asked; { ids: [] } = skipped; { ids: ["kpop", ...] } = picked
export function getMoods() {
  const saved = readJSON(MOODS_KEY, null);
  return saved && Array.isArray(saved.ids) ? saved : null;
}

export function saveMoods(ids) {
  const saved = { ids: ids.filter((id) => MOODS.some((m) => m.id === id)).slice(0, MAX_MOODS), ts: Date.now() };
  try {
    window.localStorage.setItem(MOODS_KEY, JSON.stringify(saved));
  } catch (e) {
    // blocked storage: picks still apply for this page view
  }
  return saved;
}

// Adds the picked moods' tags to a taste profile
export function addMoods(profile, ids, weight = 1) {
  MOODS.filter((m) => ids.includes(m.id)).forEach((m) =>
    m.tags.forEach((tag) => { profile[tag] = (profile[tag] || 0) + weight; }));
  return profile;
}

// Index entry / history entry -> the card shape HorizontalScroll renders
export function toCard(item) {
  const card = { name: [item.n], path: ["/game/" + item.s], thumb: item.t, slug: item.s };
  if (item.v) card.video = item.v;
  if (item.l) card.label = item.l;
  if (item.b) card.badge = item.b;
  if (item.pc) card.platform = "pc";
  return card;
}

let indexPromise = null;
export function loadIndex() {
  if (!indexPromise) {
    indexPromise = fetch("/reco-index.json")
      .then((res) => res.json())
      .then((games) => {
        // Rarer tags say more about taste ("k pop" vs "dress up"), so weight by inverse frequency.
        // Tags on fewer than 5 games ("styling", "birthday party") are one-off labels, not interests.
        const df = {};
        games.forEach((game) => game.g.forEach((tag) => { df[tag] = (df[tag] || 0) + 1; }));
        const idf = {};
        Object.keys(df).forEach((tag) => { idf[tag] = df[tag] < 5 ? 0 : Math.log(games.length / df[tag]); });
        return { games, idf };
      })
      .catch(() => {
        indexPromise = null;
        return null;
      });
  }
  return indexPromise;
}

// Taste profile: tag -> weight, recent plays count more
export function tasteProfile(history) {
  const profile = {};
  history.forEach((h, i) => {
    const recency = 1 / (1 + i * 0.25);
    (h.g || []).forEach((tag) => { profile[tag] = (profile[tag] || 0) + recency; });
  });
  return profile;
}

// Highest-scoring unplayed games for a tag profile. Index order (newest first) breaks ties.
export function recommend({ games, idf }, profile, { exclude = [], limit = 30, mobile = false } = {}) {
  const skip = new Set(exclude);
  const scored = [];
  games.forEach((game, order) => {
    if (skip.has(game.s) || (mobile && game.pc)) return;
    let score = 0;
    game.g.forEach((tag) => { if (profile[tag]) score += profile[tag] * (idf[tag] || 0); });
    if (score > 0) scored.push({ game, score: score / Math.sqrt(game.g.length), order });
  });
  scored.sort((a, b) => b.score - a.score || a.order - b.order);
  return scored.slice(0, limit).map((x) => toCard(x.game));
}

// First-visit picks with no play history yet. Each mood is ranked on its own (its first tag
// counts double, so "princess" beats a match-3 game that is only tagged "mermaid"), then the
// lists are interleaved so every picked mood shows up near the start of the row.
export function recommendForMoods({ games }, ids, { exclude = [], limit = 30, mobile = false } = {}) {
  const skip = new Set(exclude);
  const lists = MOODS.filter((m) => ids.includes(m.id)).map((mood) => {
    const scored = [];
    games.forEach((game, order) => {
      if (skip.has(game.s) || (mobile && game.pc)) return;
      let score = 0;
      mood.tags.forEach((tag, i) => { if (game.g.includes(tag)) score += i === 0 ? 2 : 1; });
      if (score > 0) scored.push({ game, score, order });
    });
    scored.sort((a, b) => b.score - a.score || a.order - b.order);
    return scored.map((x) => x.game);
  });

  const out = [];
  const seen = new Set();
  for (let i = 0; out.length < limit && lists.some((list) => i < list.length); i++) {
    lists.forEach((list) => {
      const game = list[i];
      if (game && !seen.has(game.s) && out.length < limit) {
        seen.add(game.s);
        out.push(toCard(game));
      }
    });
  }
  return out;
}

// GA4 event for clicks/impressions on personalised rows (no-op if gtag isn't loaded)
export function trackReco(action, params) {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", action, params);
  }
}
